// React, store and 3rd party
import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import moment from 'moment';

// Components Icons
import AddIcon from '@mui/icons-material/Add';
import Button from '@mui/material/Button';
import { DataGrid } from '@mui/x-data-grid';

// Store
import { breadcrumbsActions } from '@store/breadcrumbs';
import { usePermissions } from '@store/auth';
import ApplicationPageTemplate from '@dashboardComponents/ApplicationPageTemplate/ApplicationPageTemplate';
import {
  getPropertyKeys,
  paymentOptionsPropertyKeysActions,
  selectPropertyKeys,
} from '../../store/payment-options-property-keys';

import {
  PROPERTY_KEY_NEW_ROUTE,
  PROPERTY_KEY_LIST_ROUTE,
  viewPropertyKeys,
} from '../../payment-options.breadcrumbs';

import TableToolbar from '../../../../components/TableToolbar';

function PropertyKeyToolbar({ navigate, permissions }) {
  return (
    <TableToolbar>
      <Button
        disabled={!permissions?.canEditPaymentOptions}
        variant="contained"
        size="large"
        onClick={() => navigate(PROPERTY_KEY_NEW_ROUTE)}
        startIcon={<AddIcon />}>
        Add new property key
      </Button>
    </TableToolbar>
  );
}

export default function PropertyKeys() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const permissions = usePermissions();

  const [isFetchingData, setIsFetchingData] = useState(true);

  const propertyKeys = useSelector(selectPropertyKeys);

  useEffect(() => {
    dispatch(breadcrumbsActions.showMenu(true));
    dispatch(breadcrumbsActions.setBreadcrumbs(viewPropertyKeys));
    dispatch(paymentOptionsPropertyKeysActions.resetSelectedPropertyKey());
  }, [dispatch]);

  useEffect(() => {
    setIsFetchingData(true);
    dispatch(getPropertyKeys()).then(() => {
      setIsFetchingData(false);
    });
  }, [dispatch]);

  return (
    <ApplicationPageTemplate
      title="Property Keys"
      description="List of available property keys. You can also the page to add and update property keys."
      pageReady={!isFetchingData}>
      <DataGrid
        density="standard"
        style={{ background: 'white' }}
        rows={propertyKeys}
        rowsPerPageOptions={[10]}
        pageSize={10}
        disableSelectionOnClick
        disableColumnFilter
        disableColumnMenu
        disableColumnSelector
        disableDensitySelector
        components={{
          Toolbar: () => PropertyKeyToolbar({ navigate, permissions }),
        }}
        componentsProps={{
          toolbar: {
            csvOptions: { disableToolbarButton: true },
            printOptions: { disableToolbarButton: true },
            showQuickFilter: true,
            quickFilterProps: { debounceMs: 500 },
          },
        }}
        onRowClick={({ row }) => {
          navigate(`${PROPERTY_KEY_LIST_ROUTE}/${row.id}`);
        }}
        getRowId={(row) => row.key}
        columns={[
          {
            field: 'id',
            headerName: 'ID',
            sortable: false,
            headerAlign: 'center',
            align: 'center',
          },
          {
            field: 'description',
            headerName: 'Description',
            flex: 1,
            sortable: false,
            headerAlign: 'center',
            align: 'center',
          },
          {
            field: 'key',
            headerName: 'Key',
            flex: 1,
            sortable: false,
            headerAlign: 'center',
            align: 'center',
          },
          {
            field: 'sendPropertyToUi',
            headerName: 'Send Property to UI',
            flex: 1,
            sortable: false,
            headerAlign: 'center',
            align: 'center',
          },
          {
            field: 'modifiedBy',
            headerName: 'Modified By',
            flex: 1,
            sortable: false,
            headerAlign: 'center',
            align: 'center',
          },
          {
            field: 'modifiedAt',
            headerName: 'Last Modified Date',
            flex: 1,
            sortable: false,
            headerAlign: 'right',
            align: 'right',
            valueGetter: ({ row }) =>
              moment(row.modifiedAt).format('YYYY/MM/DD - HH:mm'),
          },
        ]}
      />
    </ApplicationPageTemplate>
  );
}
